import fetch from "cross-fetch"

import { EXPIRED_SESSION_MESSAGE, INVALID_ACCESS_TOKEN } from "../constants/errorMessages"
import accountActions from "../redux/account/actions"
import { store } from "../redux/store"
import { refreshAccessToken } from "./auth-util"
import config from "./config"
import { logException } from "./sentry"

let controller
let signal
let timeoutID
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

async function handle401(error, originalRequest) {
  if (error.code === INVALID_ACCESS_TOKEN) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
        .then(token => {
          originalRequest.options.headers.Authorization = `JWT ${token}`
          return fetch(originalRequest.uri, originalRequest.options).then(resp => resp.json())
        })
        .catch(err => {
          return Promise.reject(err)
        })
    }

    isRefreshing = true

    await refreshAccessToken()

    const accessToken = localStorage.getItem("accessToken")

    if (accessToken) {
      originalRequest.options.headers.Authorization = `JWT ${accessToken}`

      processQueue(null, accessToken)

      isRefreshing = false
      return fetch(originalRequest.uri, originalRequest.options).then(resp => resp.json())
    }
  }

  return false
}

export const httpAbort = () => {
  clearTimeout(timeoutID)
  controller.abort()
}
const clearSignalTimer = () => timeoutID && clearTimeout(timeoutID)
const api = (uri, options = {}, headers) => {
  controller = new AbortController()
  signal = controller.signal
  clearSignalTimer()
  timeoutID = setTimeout(() => controller.abort(), 180000)
  options.headers = {
    ...headers,
    "Content-Type": "application/json",
  }
  options.mode = "cors"
  options.cache = "default"

  const accessToken = localStorage.getItem("accessToken")

  if (accessToken) {
    options.headers = {
      ...options.headers,
      Authorization: `JWT ${accessToken}`,
    }
  }
  options.signal = signal

  const originalRequest = { uri: config.API_BASE_URL + uri, options }

  return fetch(config.API_BASE_URL + uri, options)
    .then(response => {
      if (
        !response.headers.get("content-type").includes("application/json") &&
        response.status >= 200 &&
        response.status < 300
      ) {
        return { message: "Not a json response" }
      }
      return response.json().then(json => {
        clearSignalTimer()
        if (response.status >= 200 && response.status < 300) {
          return json
        }
        if (response.status === 401) {
          if (!json.code) {
            return Promise.reject(json)
          }
          if (json.code && json.code !== INVALID_ACCESS_TOKEN) {
            return store.dispatch(accountActions.setTokenError(EXPIRED_SESSION_MESSAGE))
          }
          return handle401(json, originalRequest)
        }
        return Promise.reject(json)
      })
    })
    .catch(error => {
      logException({
        error: JSON.stringify(error),
        level: "warning",
        fingerprint: Object.keys(error)[0],
        extras: {
          stateInfo: store.getState(),
        },
      })
      return Promise.reject(error)
    })
}

export const httpPost = (url, body, headers) => {
  return api(
    url,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
    headers,
  )
}
export const httpPut = (url, body) => {
  return api(url, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}

export const httpPatch = (url, body) => {
  return api(url, {
    method: "PATCH",
    body: JSON.stringify(body),
  })
}

export const httpGet = (url, headers) => {
  return api(
    url,
    {
      method: "GET",
    },
    headers,
  )
}
export const httpDelete = url => {
  return api(url, {
    method: "DELETE",
  })
}
